import store from '@/store'

/**
 * 权限判断工具函数
 * 基于 Vuex 状态管理的权限控制
 */

/**
 * 检查是否有指定权限
 * @param {string} code - 权限码
 * @returns {boolean} 是否有权限
 * @example
 * // 检查单个权限
 * if (hasPermission('customer:view')) {
 *   console.log('有查看客户的权限');
 * }
 */
export function hasPermission(code) {
  return store.getters.hasPermission(code);
}

/**
 * 检查是否有所有指定权限
 * @param {Array<string>} codes - 权限码数组
 * @returns {boolean} 是否有所有权限
 * @example
 * // 检查多个权限（全部满足）
 * if (hasPermissions(['customer:view', 'customer:edit'])) {
 *   console.log('有查看和编辑客户的权限');
 * }
 */
export function hasPermissions(codes) {
  return store.getters.hasPermissions(codes);
}

/**
 * 检查是否有任意一个指定权限
 * @param {Array<string>} codes - 权限码数组
 * @returns {boolean} 是否有任意一个权限
 * @example
 * // 检查多个权限（任一满足）
 * if (hasAnyPermission(['customer:view', 'customer:edit'])) {
 *   console.log('有查看或编辑客户的权限');
 * }
 */
export function hasAnyPermission(codes) {
  return store.getters.hasAnyPermission(codes);
}

/**
 * 获取所有权限列表
 * @returns {Array<string>} 权限码数组
 * @example
 * const permissions = getPermissions();
 * console.log('当前用户权限:', permissions);
 */
export function getPermissions() {
  return store.getters.getPermissions;
}

/**
 * 检查是否有权限分类的权限
 * @param {string} category - 权限分类前缀
 * @returns {boolean} 是否有该分类的任意权限
 * @example
 * // 检查是否有客户管理相关权限
 * if (hasPermissionCategory('customer')) {
 *   console.log('有客户管理相关权限');
 * }
 */
export function hasPermissionCategory(category) {
  const permissions = getPermissions();
  return permissions.some(code => code.startsWith(category + ':'));
}

/**
 * 获取指定分类的权限列表
 * @param {string} category - 权限分类前缀
 * @returns {Array<string>} 该分类的权限码数组
 * @example
 * // 获取客户管理相关权限
 * const customerPermissions = getPermissionsByCategory('customer');
 * console.log('客户管理权限:', customerPermissions);
 */
export function getPermissionsByCategory(category) {
  const permissions = getPermissions();
  return permissions.filter(code => code.startsWith(category + ':'));
}

/**
 * 异步获取并更新权限
 * @returns {Promise<Array<string>>} 权限码数组
 * @example
 * // 刷新权限
 * try {
 *   const permissions = await fetchAndUpdatePermissions();
 *   console.log('更新后的权限:', permissions);
 * } catch (error) {
 *   console.error('权限更新失败:', error);
 * }
 */
export async function fetchAndUpdatePermissions() {
  try {
    return await store.dispatch('fetchPermissions');
  } catch (error) {
    console.error('获取权限失败:', error);
    throw error;
  }
}

/**
 * 清除权限缓存
 * @example
 * // 退出登录时清除权限
 * clearPermissions();
 */
export function clearPermissions() {
  store.dispatch('clearPermissions');
}

/**
 * 权限检查装饰器函数
 * @param {string|Array<string>} codes - 权限码或权限码数组
 * @param {boolean} requireAll - 是否需要所有权限（默认false，表示有任意一个即可）
 * @returns {Function} 装饰器函数
 * @example
 * // 装饰器方式检查权限
 * const checkCustomerPermission = requirePermission('customer:view');
 * const result = checkCustomerPermission(() => {
 *   console.log('有权限执行');
 *   return '执行结果';
 * });
 */
export function requirePermission(codes, requireAll = false) {
  return function(func) {
    return function(...args) {
      const hasAccess = Array.isArray(codes) 
        ? (requireAll ? hasPermissions(codes) : hasAnyPermission(codes))
        : hasPermission(codes);
      
      if (hasAccess) {
        return func.apply(this, args);
      } else {
        console.warn('权限不足:', codes);
        return null;
      }
    };
  };
}

/**
 * 权限检查混入对象（用于Vue组件）
 * @example
 * // 在Vue组件中使用
 * export default {
 *   mixins: [permissionMixin],
 *   methods: {
 *     handleEdit() {
 *       if (this.hasPermission('customer:edit')) {
 *         // 执行编辑操作
 *       }
 *     }
 *   }
 * }
 */
export const permissionMixin = {
  methods: {
    hasPermission,
    hasPermissions,
    hasAnyPermission,
    getPermissions,
    hasPermissionCategory,
    getPermissionsByCategory,
    fetchAndUpdatePermissions,
    clearPermissions
  }
};

export default {
  hasPermission,
  hasPermissions,
  hasAnyPermission,
  getPermissions,
  hasPermissionCategory,
  getPermissionsByCategory,
  fetchAndUpdatePermissions,
  clearPermissions,
  requirePermission,
  permissionMixin
}; 


// WEBPACK FOOTER //
// ./src/utils/permission.js